import { tv } from '@/ui/utils'

/**
 * The brand's buttons: rectangular, 2px corners, 44px tall, a Maison Neue Bold label.
 *
 * Four kinds. A black `primary`; a coral `accent` for the one action a screen leads with; an
 * outlined `secondary`; and a `link` that is only its label. Hover and focus go Digital Blue,
 * which is the only time that colour appears at rest on nothing. Disabled is the library's
 * inactive treatment — light-grey fill, grey label, grey outline — never opacity, so a disabled
 * button still reads as a button and not as a ghost.
 *
 * `size="sm"` is the tertiary chip: 28px, a hairline, black when it is the selected one. It is
 * the picker's time and week chip, and every small in-row action.
 *
 * Width hugs content, as the brand's do. `fullWidth` is for forms, where the deck lays the
 * submit across the column.
 */
export const buttonStyles = tv({
	base: 'group flex flex-row items-center justify-center rounded-full border web:transition-colors web:focus-visible:outline-none cursor-pointer',
	variants: {
		variant: {
			primary: 'bg-ink border-ink hover:bg-focus hover:border-focus',
			accent: 'bg-accent border-accent hover:bg-focus hover:border-focus',
			secondary: 'bg-transparent border-ink hover:border-focus',
			link: 'bg-transparent border-transparent px-0 h-auto',
			// On an ink note: the pink fill is the lead, the ghost is the other one.
			ground: 'bg-ground border-ground hover:bg-focus hover:border-focus',
			// Over a photograph: the blush fill is the lead, the ghost is the other one.
			//
			// An outline and no fill, which is `secondary`'s shape in white rather than a third
			// idea. A translucent wash only reads as a button where the picture behind it happens
			// to be dark — over a bright patch it dissolves and leaves the label floating — and a
			// photograph makes no promise about what is behind any part of it. A hairline holds at
			// every brightness, and being the shape this app already spends on second actions it
			// stays plainly second to the fill above it.
			ghost: 'bg-transparent border-white/70 hover:bg-white/15',
			blush: 'bg-blush border-blush hover:bg-focus hover:border-focus',
		},
		size: {
			default: 'min-h-11 px-5',
			sm: 'min-h-7 px-3',
			lg: 'min-h-[52px] px-5',
		},
		fullWidth: {
			true: 'w-full',
			false: 'self-start',
		},
		selected: {
			true: '',
		},
		disabled: {
			true: 'cursor-default',
		},
	},
	compoundVariants: [
		// Disabled: the inactive treatment, identical for every filled kind.
		{
			variant: ['primary', 'accent', 'ground', 'ghost', 'blush'],
			disabled: true,
			class: 'bg-surface border-muted hover:bg-surface hover:border-muted',
		},
		{ variant: 'secondary', disabled: true, class: 'border-muted hover:border-muted' },
		{ variant: 'link', disabled: true, class: '' },
		// A selected chip is filled black.
		{ variant: 'secondary', selected: true, class: 'bg-ink border-ink hover:bg-ink' },
		// A link is its label, so it sits flush with whatever it is set beside. The size variants
		// carry the pill's side padding and are declared after `variant`, so `px-5` was winning the
		// merge and indenting every link by twenty points from the text above it. Restated here,
		// where a compound runs last and for every size.
		{ variant: 'link', class: 'h-auto min-h-0 px-0' },
		{ variant: 'link', fullWidth: true, class: 'w-auto self-start' },
	],
	defaultVariants: {
		variant: 'primary',
		size: 'default',
		fullWidth: false,
		selected: false,
		disabled: false,
	},
})

export const buttonTextStyles = tv({
	base: 'web:whitespace-nowrap font-bold text-body1 tracking-normal',
	variants: {
		variant: {
			primary: 'text-white',
			accent: 'text-white',
			secondary: 'text-ink group-hover:text-focus',
			link: 'text-ink underline underline-offset-4 group-hover:text-focus',
			ground: 'text-ink group-hover:text-white',
			ghost: 'text-white',
			blush: 'text-ink group-hover:text-white',
		},
		size: {
			default: 'text-body1',
			sm: 'text-body2',
			lg: 'text-body0',
		},
		selected: {
			true: '',
		},
		disabled: {
			true: '',
		},
		spacing: {
			normal: 'tracking-normal',
			wide: 'tracking-[.15em]',
		},
	},
	compoundVariants: [
		{
			variant: ['primary', 'accent', 'secondary', 'ground', 'ghost', 'blush'],
			disabled: true,
			class: 'text-muted',
		},
		{ variant: 'link', disabled: true, class: 'text-muted' },
		{ variant: 'secondary', selected: true, class: 'text-white group-hover:text-white' },
	],
	defaultVariants: {
		variant: 'primary',
		size: 'default',
		spacing: 'normal',
		selected: false,
		disabled: false,
		isLoading: false,
	},
})
